/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/**
 * Formats a date string from "DD/MM/YYYY" to either
 * - "MM/DD/YYYY" (default)
 * - or "YYYY-MM-DD" (if track = true)
 */
import {
  AALocale,
  aaLocalFrom,
  DateTimeParser,
  DateTimeFormat,
  parseDate as parseDateByFormat,
} from '@airasia-phoenix/react-native/utils';
// Import each locale from its own path: the `date-fns/locale` barrel pulls
// every locale date-fns ships into the bundle (~800 KB).
import { enGB } from 'date-fns/locale/en-GB';
import { id } from 'date-fns/locale/id';
import { ja } from 'date-fns/locale/ja';
import { km } from 'date-fns/locale/km';
import { ko } from 'date-fns/locale/ko';
import { ms } from 'date-fns/locale/ms';
import { th } from 'date-fns/locale/th';
import { tr } from 'date-fns/locale/tr';
import { vi } from 'date-fns/locale/vi';
import { zhCN } from 'date-fns/locale/zh-CN';
import { zhTW } from 'date-fns/locale/zh-TW';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';

export const formatTime = (isoDate: string, locale?: string): string => {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return '';
  }

  return DateTimeParser.parseDate(date)
    .local(locale ? aaLocalFrom(locale) : aaLocalFrom('en-gb'))
    .formatTime();
};

type FormatDateTimeProps = {
  date: string | Date | null | undefined;
  locale?: AALocale;
  format?: DateTimeFormat;
};

export const formatDateTime = ({
  date,
  locale = AALocale.EN_GB,
  format = DateTimeFormat.DATE_TIME_WITH_MONTH,
}: FormatDateTimeProps): string => {
  if (!date) return '';
  try {
    const parsedDate = date instanceof Date ? date : new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date.toString();
    }

    return DateTimeParser.parseDate(parsedDate).local(locale).format(format);
  } catch (error) {
    return date.toString();
  }
};

export const getDayOffsetSuffix = (
  departAt: string,
  arriveAt: string,
): string => {
  const datePartPattern = /^(\d{4})-(\d{2})-(\d{2})T/;
  const departDatePart = departAt.match(datePartPattern)?.[0].slice(0, 10);
  const arriveDatePart = arriveAt.match(datePartPattern)?.[0].slice(0, 10);

  if (departDatePart && arriveDatePart) {
    const departDay = parseDateByFormat(departDatePart, 'yyyy-MM-dd');
    const arriveDay = parseDateByFormat(arriveDatePart, 'yyyy-MM-dd');
    const dayDifference = Math.round(
      (arriveDay.getTime() - departDay.getTime()) / (24 * 60 * 60 * 1000),
    );

    return dayDifference > 0 ? `+${dayDifference}` : '';
  }

  const departDate = new Date(departAt);
  const arriveDate = new Date(arriveAt);

  if (isNaN(departDate.getTime()) || isNaN(arriveDate.getTime())) {
    return '';
  }

  const departDay = new Date(
    departDate.getUTCFullYear(),
    departDate.getUTCMonth(),
    departDate.getUTCDate(),
  );
  const arriveDay = new Date(
    arriveDate.getUTCFullYear(),
    arriveDate.getUTCMonth(),
    arriveDate.getUTCDate(),
  );

  const dayDifference = Math.round(
    (arriveDay.getTime() - departDay.getTime()) / (24 * 60 * 60 * 1000),
  );

  return dayDifference > 0 ? `+${dayDifference}` : '';
};

export const normalizeGmtOffset = (gmt?: string | null): string | undefined => {
  const match = gmt?.trim().match(/(?:GMT)?([+-])(\d{1,2})(?::?(\d{2}))?/i);
  if (!match) return undefined;

  const [, sign, hourText, minuteText = '0'] = match;
  const hours = Number(hourText);
  const minutes = Number(minuteText);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined;

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}`;
};

export const formatAirportLocalIso = ({
  localTime,
  utcTime,
  gmtOffset,
  includeTimezone = true,
}: {
  localTime?: string;
  utcTime?: string;
  gmtOffset?: string | null;
  includeTimezone?: boolean;
}): string => {
  const offset = normalizeGmtOffset(gmtOffset);
  if (!utcTime || !offset) return localTime ?? '';

  const date = new Date(utcTime);
  if (Number.isNaN(date.getTime())) return localTime ?? '';

  const formatter = DateTimeParser.parseDate(date)
    .timeZoneOffset(offset)
    .local(AALocale.EN_GB);
  const [day, month, year] = formatter.formatDate().split('/');
  const timezoneSuffix = includeTimezone ? '.000Z' : '';

  return `${year}-${month}-${day}T${formatter.formatTime()}:00${timezoneSuffix}`;
};

export const getDate = (
  date?: string | null,
  track: boolean = false,
): string => {
  if (!date) {
    return '';
  }

  const parts = date.split('/');
  if (parts.length !== 3) {
    console.warn('Invalid date format. Expected "DD/MM/YYYY"');
    return '';
  }

  const [day, month, year] = parts;
  return track ? `${year}-${month}-${day}` : `${month}/${day}/${year}`;
};

/**
 * Converts an ISO date string (e.g., "2024-05-23T13:23:16.896Z")
 * into a readable date like "23 May 2024, 13:23 (KUL)"
 */
export const getDateString = (
  dateString: string,
  departTimeZone?: string,
): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return '';
  }

  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const day = date.getUTCDate();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  const formattedDate = `${day} ${month} ${year}, ${hours
    .toString()
    .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  return departTimeZone
    ? `${formattedDate} (${departTimeZone})`
    : formattedDate;
};

export const convertUTCToGMT = (inputDate: string | number | Date) => {
  const dateObj = new Date(inputDate);
  return new Date(
    dateObj.getUTCFullYear(),
    dateObj.getUTCMonth(),
    dateObj.getUTCDate(),
    dateObj.getUTCHours(),
    dateObj.getUTCMinutes(),
    dateObj.getUTCSeconds(),
    dateObj.getUTCMilliseconds(),
  );
};

const ISO_DATE_PART_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

export const getFlightLocalDate = (dateString?: string | null): Date | null => {
  if (!dateString) return null;

  const datePartMatch = dateString.match(ISO_DATE_PART_PATTERN);

  if (datePartMatch) {
    const [, year, month, day] = datePartMatch;
    const yearNumber = Number(year);
    const monthNumber = Number(month);
    const dayNumber = Number(day);
    const date = new Date(yearNumber, monthNumber - 1, dayNumber);

    if (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() === yearNumber &&
      date.getMonth() === monthNumber - 1 &&
      date.getDate() === dayNumber
    ) {
      return date;
    }
  }

  const parsedDate = new Date(dateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return convertUTCToGMT(parsedDate);
};

export const formatFlightLocalDate = (
  dateString?: string | null,
  dateFormat = 'EEE, d MMM yyyy',
  locale = 'en-GB',
): string => {
  const date = getFlightLocalDate(dateString);

  if (!date) {
    return '';
  }

  return format(date, dateFormat, {
    locale: getLocale(locale),
  });
};

export const getLocale = (locale: string): Locale => {
  switch (locale.toLowerCase()) {
    case 'en-gb':
      return enGB;
    case 'ja-jp':
      return ja;
    case 'vi-vn':
      return vi;
    case 'th-th':
      return th;
    case 'ms-my':
      return ms;
    case 'zh-cn':
    case 'zh-hk':
      return zhCN;
    case 'zh-tw':
      return zhTW;
    case 'ko-kr':
      return ko;
    case 'id-id':
      return id;
    case 'tr-tr':
      return tr;
    case 'km-kh':
      return km;
    default:
      return enGB;
  }
};
